import React from "react";
import { Alert, Box, Chip, Grid, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";

function formatMonthLabel(key) {
  if (!key) return "—";
  const [y, m] = String(key).split("-").map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function monthsFromRow(row) {
  if (Array.isArray(row?.monthBreakdown) && row.monthBreakdown.length) {
    return row.monthBreakdown.filter((m) => m && (Number(m.paid) || Number(m.lop) || Number(m.days)));
  }
  const paid = row?.paidByMonth && typeof row.paidByMonth === "object" ? row.paidByMonth : {};
  const lop = row?.lopByMonth && typeof row.lopByMonth === "object" ? row.lopByMonth : {};
  const keys = [...new Set([...Object.keys(paid), ...Object.keys(lop)])]
    .filter((k) => /^\d{4}-\d{2}$/.test(k))
    .sort();
  return keys.map((k) => ({
    monthKey: k,
    monthLabel: formatMonthLabel(k),
    paid: Number(paid[k]) || 0,
    lop: Number(lop[k]) || 0,
    days: (Number(paid[k]) || 0) + (Number(lop[k]) || 0),
  }));
}

export function leaveTypeLabel(leaveType) {
  const n = String(leaveType || "").trim();
  if (!n) return "Leave";
  const k = n.toLowerCase();
  if (k.includes("comp")) return "Compensatory Off";
  if (k === "lop" || k.includes("loss of pay")) return "Loss of Pay (LOP)";
  if (k.includes("annual") || k.includes("earned")) return "Monthly leave";
  if (k.includes("sick")) return "Sick Leave";
  if (k.includes("casual")) return "Casual Leave";
  if (k.includes("other")) return "Other Leave";
  if (k.includes("maternity")) return "Maternity Leave";
  if (k.includes("paternity")) return "Paternity Leave";
  if (k.includes("marriage")) return "Marriage Leave";
  if (k.includes("bereavement")) return "Bereavement Leave";
  return n;
}

export function isAutoAbsentLeave(row) {
  return Boolean(row?.autoGenerated || row?.autoAbsentForDate);
}

export function AutoAbsentChip({ size = "small" }) {
  return (
    <Chip
      size={size}
      label="Auto-absent"
      color="warning"
      sx={{ fontWeight: 700, height: size === "small" ? 22 : undefined }}
    />
  );
}

export function deductionSources(row) {
  const a = leaveAllocationFromRow(row);
  if (!a) return [];
  const type = String(row?.leaveType || "").toLowerCase();
  const sources = [];
  if (type.includes("comp")) {
    sources.push({ key: "compOff", label: "Compensatory Off", days: a.total });
    return sources;
  }
  if (a.paid > 0) {
    sources.push({ key: "monthly", label: "Monthly leave (1.5/month pool)", days: a.paid });
  }
  if (a.lop > 0) {
    sources.push({ key: "lop", label: "Loss of Pay (LOP)", days: a.lop });
  }
  if (!sources.length) {
    sources.push({ key: "none", label: leaveTypeLabel(row?.leaveType), days: a.total });
  }
  return sources;
}

export function leaveAllocationFromRow(row) {
  if (!row) return null;
  const total = Number(row.totalDays) || 0;
  const paid = row.paidDays != null && row.paidDays !== "" ? Number(row.paidDays) : null;
  const lop = row.lopDays != null && row.lopDays !== "" ? Number(row.lopDays) : null;
  const type = String(row.leaveType || "").toLowerCase();
  const isLop = type === "lop" || type.includes("loss of pay");
  const isComp = type.includes("comp");
  return {
    total,
    paid: paid != null ? paid : isLop || isComp ? 0 : total,
    lop: lop != null ? lop : isLop ? total : 0,
    before: row.monthlyLeaveBefore,
    after: row.monthlyLeaveAfter,
    summary: row.allocationSummary || "",
    status: row.status,
    months: monthsFromRow(row),
  };
}

export function LeaveAllocationBreakdown({ row, compact = false }) {
  const a = leaveAllocationFromRow(row);
  if (!a) return null;
  const type = String(row?.leaveType || "").toLowerCase();
  const sources = deductionSources(row);
  if (type.includes("comp")) {
    return (
      <Box>
        <Typography variant="body2" fontWeight={700}>
          Deducted from: Compensatory Off ({a.total} day{a.total === 1 ? "" : "s"})
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ minWidth: compact ? 220 : 280 }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.35 }}>
        Deducted from
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.4 }}>
        {sources.map((s) => (
          <Chip
            key={s.key}
            size="small"
            color={s.key === "lop" ? "error" : s.key === "compOff" ? "warning" : "success"}
            label={`${s.label} · ${s.days}`}
          />
        ))}
      </Stack>
      {a.months.map((m) => (
        <Typography key={m.monthKey || m.monthLabel} variant="caption" color="text.secondary" display="block">
          {m.monthLabel}: {m.paid > 0 ? `${m.paid} monthly leave` : "no monthly leave"}
          {m.lop > 0 ? ` · ${m.lop} LOP on ${m.monthLabel} slip` : " · no LOP that month"}
        </Typography>
      ))}
      {a.before != null && (
        <Typography variant="caption" color="text.secondary" display="block">
          Monthly leave pool {a.before} → {a.after}
        </Typography>
      )}
      {a.summary && !compact && a.months.length === 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
          {a.summary}
        </Typography>
      )}
    </Box>
  );
}

function StatCard({ label, value, hint, accent, soft }) {
  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        bgcolor: soft,
        border: "1px solid",
        borderColor: `${accent}30`,
        height: "100%",
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: "text.primary", lineHeight: 1.1, mt: 0.5 }}>
        {value}
      </Typography>
      {hint && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{hint}</Typography>}
    </Box>
  );
}

export function MonthlyLeaveSummaryCards({ monthlyLeave, lopDaysTaken, lopYearToDate, compOffAvailable, loading, hideIntro = false }) {
  if (loading || !monthlyLeave) return null;
  return (
    <Box sx={{ mb: hideIntro ? 0 : 2.5 }}>
      {!hideIntro && (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Monthly leave up to {monthlyLeave.monthLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {monthlyLeave.rate} days are added each month ({monthlyLeave.monthsAccrued} × {monthlyLeave.rate} = {monthlyLeave.accrued} accrued this year, cap {monthlyLeave.yearCap}).
            Unused days carry forward within the year. Extra days beyond remaining leave become LOP for that month.
          </Typography>
        </>
      )}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            label="Accrued"
            value={monthlyLeave.accrued}
            hint={`up to ${monthlyLeave.monthLabel}`}
            accent="#6C50BF"
            soft="#F4EDFA"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            label="Taken"
            value={monthlyLeave.taken}
            hint="paid monthly leave used"
            accent="#5C85FF"
            soft="#EDF3FA"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            label="Remaining"
            value={monthlyLeave.remaining}
            hint="available this year"
            accent="#39C645"
            soft="#EDFAF2"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            label="LOP this month"
            value={lopDaysTaken ?? 0}
            hint={
              monthlyLeave?.monthLabel
                ? `${monthlyLeave.monthLabel}${lopYearToDate ? ` · ${lopYearToDate} LOP this year` : ""}`
                : "unpaid extra leave on this month's slip"
            }
            accent="#DC2626"
            soft="#FEE2E2"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            label="Comp-off"
            value={compOffAvailable ?? 0}
            hint="days available"
            accent="#D97706"
            soft="#FFF4E6"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

const TYPE_CARD_COLORS = [
  { accent: "#6C50BF", soft: "#F4EDFA" },
  { accent: "#39C645", soft: "#EDFAF2" },
  { accent: "#5C85FF", soft: "#EDF3FA" },
  { accent: "#D97706", soft: "#FFF4E6" },
  { accent: "#0F766E", soft: "#E6F6F4" },
  { accent: "#BE185D", soft: "#FCE7F3" },
];

export function LeaveTypeDistributionCards({ types = [] }) {
  const rows = (types || []).filter((t) => t && !t.isLop);
  if (!rows.length) return null;
  const poolRows = rows.filter((t) => t.pool);
  const otherRows = rows.filter((t) => !t.pool);
  const sharedRemaining = poolRows[0]?.remaining;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        By leave type
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Annual, Casual, and Sick are only labels for why leave was taken. They all deduct from one remaining pool
        {sharedRemaining != null ? ` (${sharedRemaining} days left)` : ""}. Comp-off is a separate balance.
      </Typography>
      <Grid container spacing={2}>
        {poolRows.map((t, i) => {
          const color = TYPE_CARD_COLORS[i % TYPE_CARD_COLORS.length];
          return (
            <Grid item xs={6} sm={4} md={3} key={t.code || t.name}>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor: color.soft,
                  border: "1px solid",
                  borderColor: `${color.accent}30`,
                  height: "100%",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: color.accent }}>
                  {t.name}
                </Typography>
                <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, mt: 0.5 }}>{t.used}</Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>days used this year</Typography>
              </Box>
            </Grid>
          );
        })}
        {otherRows.map((t, i) => {
          const color = TYPE_CARD_COLORS[(poolRows.length + i) % TYPE_CARD_COLORS.length];
          return (
            <Grid item xs={6} sm={4} md={3} key={t.code || t.name}>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor: color.soft,
                  border: "1px solid",
                  borderColor: `${color.accent}30`,
                  height: "100%",
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: color.accent }}>
                  {t.name}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 0.75 }} alignItems="baseline">
                  <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{t.used}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>used this year</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: color.accent }}>{t.remaining}</Typography>
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>remaining</Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export function ManagerMonthlyLeaveTable({ snapshots = {} }) {
  const rows = Object.values(snapshots || {});
  if (!rows.length) return null;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        Team monthly leave
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        1.5 days accrue each month and unused days add up. Extra leave becomes LOP for that month.
      </Typography>
      <Paper variant="outlined" sx={{ overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Up to</TableCell>
              <TableCell align="right">Accrued</TableCell>
              <TableCell align="right">Taken</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell align="right">LOP this month</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={m.employeeid}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{m.name || "—"}</Typography>
                  <Typography variant="caption" color="text.secondary">{m.employeeid}</Typography>
                </TableCell>
                <TableCell>{m.monthLabel}</TableCell>
                <TableCell align="right">{m.accrued}</TableCell>
                <TableCell align="right">{m.taken}</TableCell>
                <TableCell align="right">{m.remaining}</TableCell>
                <TableCell align="right">{m.lopDaysTaken ?? 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

export function LeaveApplyAllocationPreview({ allocation, monthlyLeave, chargeableDays, leaveType }) {
  if (!allocation && !monthlyLeave) return null;
  const isLop = String(leaveType || "").toLowerCase() === "lop";
  const isComp = String(leaveType || "").toLowerCase().includes("comp");
  if (isComp) return null;
  const severity = allocation?.lopDays > 0 || isLop ? "warning" : "info";
  return (
    <Alert severity={severity} sx={{ borderRadius: 2 }}>
      {monthlyLeave && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Monthly leave up to {monthlyLeave.monthLabel}: <strong>{monthlyLeave.remaining}</strong> remaining
          ({monthlyLeave.accrued} accrued − {monthlyLeave.taken} taken).
        </Typography>
      )}
      {allocation && (
        <>
          <Typography variant="body2" fontWeight={700}>
            Deducted from: {allocation.paidDays} monthly leave
            {allocation.lopDays > 0 ? ` + ${allocation.lopDays} LOP` : ""}
            {` (${chargeableDays} working day${chargeableDays === 1 ? "" : "s"})`}
          </Typography>
          {Array.isArray(allocation.monthBreakdown) && allocation.monthBreakdown.length > 0 && (
            <Box sx={{ mt: 0.75 }}>
              {allocation.monthBreakdown.map((m) => (
                <Typography key={m.monthKey} variant="caption" display="block">
                  {m.monthLabel}: {m.paid} monthly leave deducted
                  {m.lop > 0
                    ? ` · ${m.lop} LOP on ${m.monthLabel} salary slip`
                    : ` · no LOP on ${m.monthLabel} slip`}
                </Typography>
              ))}
            </Box>
          )}
          {allocation.summary && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {allocation.summary}
            </Typography>
          )}
        </>
      )}
    </Alert>
  );
}
